import { MAX_CHARACTERS_MD, SELECTABLE_FOLDER_ICONS } from "../../constants";
import type { VoidComponent } from "solid-js";
import { createUniqueId } from "solid-js";
import posthog from "posthog-js";
import InputModal from "../../components/modal/InputModal";
import ApiService from "../../services/ApiService";
import { POSTHOG_EVENTS } from "../../services/PostHogService";
import { ErrorMessage, Header } from "../../components/inputs/Input";
import IconSelect from "../../components/inputs/IconSelect";
import TextAreaInput from "../../components/inputs/TextAreaInput";
import { CompactTextInput } from "../../components/inputs/TextInput";
import { useTranslation } from "../../state/i18n";
import { useNavigate } from "@solidjs/router";
import GenericFailureModal from "../GenericFailureModal";
import type { CreateFolder } from "../../types/Folder";
import { createFolderSchema } from "../../types/Folder";
import { useModalManager } from "../../components/modal/ModalManager";
import type CustomerId from "../../state/CustomerId";

type CreateFolderModalProps = {
  customerId: CustomerId;
};

const CreateFolderModal: VoidComponent<CreateFolderModalProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { replace } = useModalManager();
  const nameInputId = createUniqueId();
  const createFolderMutation = ApiService.createFolder();

  return (
    <InputModal<CreateFolder>
      variant="default"
      title={t(($) => $.CREATE_FOLDER_MODAL_TITLE)}
      titleIcon="folder"
      initialValue={{
        icon: "folder" as const,
        name: "",
      }}
      confirmLabel={t(($) => $.CREATE_FOLDER_MODAL_ACTIONS_CREATE)}
      confirmLabelSlow={t(($) => $.CREATE_FOLDER_MODAL_ACTIONS_CREATE_SLOW)}
      cancelLabel={t(($) => $.CREATE_FOLDER_MODAL_ACTIONS_CANCEL)}
      onConfirm={(value) =>
        new Promise((resolve, reject) => {
          createFolderMutation.mutate(value, {
            onSuccess: (folder) => {
              posthog.capture(POSTHOG_EVENTS.FOLDER_CREATED);
              navigate(`/customers/${props.customerId}/folders/${folder.id}`);
              resolve();
            },
            onError: (err) => {
              replace({
                content: () => (
                  <GenericFailureModal title={t(($) => $.CREATE_FOLDER_FAILURE_MODAL_TITLE)} error={err} />
                ),
              });
              reject(err);
            },
          });
        })
      }
      schema={createFolderSchema}
    >
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <Header id={nameInputId} label={t(($) => $.CREATE_FOLDER_MODAL_INPUT_ICON_AND_NAME)} required />
            <div class="flex gap-1">
              <IconSelect
                icons={SELECTABLE_FOLDER_ICONS}
                value={value("icon")!}
                disabled={false}
                size="sm"
                onChange={(value) => updateValue("icon", value)}
                errorMessage={error("icon")}
              />
              <CompactTextInput
                id={nameInputId}
                value={value("name")}
                onBlur={(e) => {
                  updateValue("name", e.target.value);
                }}
                onInput={(e) => {
                  updateValue("name", e.target.value);
                }}
                errorMessage={error("name")}
              />
            </div>
            <ErrorMessage errorMessage={error("icon")} />
            <ErrorMessage errorMessage={error("name")} />
          </div>
          <TextAreaInput
            label={t(($) => $.CREATE_FOLDER_MODAL_INPUT_DESCRIPTION)}
            value={value("description")}
            maxLength={MAX_CHARACTERS_MD}
            onBlur={(e) => {
              updateValue("description", e.target.value);
            }}
            onInput={(e) => {
              updateValue("description", e.target.value);
            }}
            errorMessage={error("description")}
          />
        </div>
      )}
    </InputModal>
  );
};

export default CreateFolderModal;
